import { useEffect, useMemo, useState } from 'react';
import { AppDialog } from '../../components/AppDialog';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { GridSearch } from '../../components/GridSearch';
import { singleSelection } from './itemTestsModel';
import type { CrudField } from './moduleCrud';
import './GridLookupModal.css';

/** One fetched option: id + the display cells (cell 0 is the name). */
export interface GridLookupRow {
  id: string;
  cells: string[];
}

interface DialogRow {
  id: string;
  [col: string]: string;
}

/**
 * Legacy ASPxGridLookup for an FK field — a text box plus Find / Clear buttons
 * that open a MODAL grid picker (not the inline dropdown the LookupCombo shows).
 * Matches the legacy Client lookup (image 931): a centered dialog with a Search
 * box, the Client / Address / Phone grid, single-select (row click picks, or tick
 * + OK), OK / Cancel + pager. Opt-in via CrudField.lookupModal; the rows + columns
 * are exactly what the inline LookupCombo used, so it is a drop-in replacement.
 */
export function GridLookupModal({
  field,
  rows,
  value,
  labelText,
  readOnly,
  showEmpty,
  onPick,
  onClear,
  onEditRow,
}: {
  field: CrudField;
  rows: GridLookupRow[];
  /** The selected id (''/none when empty). */
  value: string;
  /** The selected row's display name, shown in the text box. */
  labelText: string;
  readOnly: boolean;
  showEmpty?: boolean;
  onPick: (id: string, cells: string[]) => void;
  onClear: () => void;
  /** When set, a per-row EDIT pencil column is shown before the first column
   *  (legacy parity); clicking it edits that row's record. */
  onEditRow?: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string | number>>(new Set());

  const cols = field.lookupColumns ?? ['Name'];
  const columns: AppColumn<DialogRow>[] = useMemo(() => {
    const dataCols: AppColumn<DialogRow>[] = cols.map((c, i) => ({
      field: `c${i}`,
      header: c,
      ...(i === 0 ? { width: 280 } : {}),
    }));
    if (!onEditRow) return dataCols;
    // Legacy per-row edit pencil (before the first column). stopPropagation so it
    // doesn't also fire the row's pick (onRowClick).
    const editCol: AppColumn<DialogRow> = {
      field: '__edit',
      header: '',
      width: 40,
      sortable: false,
      render: (row) => (
        <button
          type="button"
          className="gl-row-edit"
          title="Edit"
          aria-label="Edit"
          onClick={(e) => {
            e.stopPropagation();
            onEditRow(row.id);
          }}
        >
          <i className="ti ti-edit" />
        </button>
      ),
    };
    return [editCol, ...dataCols];
  }, [cols, onEditRow]);
  const dialogRows: DialogRow[] = useMemo(
    () =>
      rows.map((r) => {
        const o: DialogRow = { id: r.id };
        r.cells.forEach((cell, i) => {
          o[`c${i}`] = cell ?? '';
        });
        return o;
      }),
    [rows],
  );
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return dialogRows;
    return dialogRows.filter((r) => cols.some((_, i) => String(r[`c${i}`] ?? '').toLowerCase().includes(q)));
  }, [dialogRows, search, cols]);

  // Each opening starts clean, with the current value ticked.
  useEffect(() => {
    if (!open) return;
    setSearch('');
    setSelected(value ? new Set([value]) : new Set());
  }, [open, value]);

  const cellsById = useMemo(() => new Map(rows.map((r) => [r.id, r.cells] as const)), [rows]);
  const pick = (id: string) => {
    onPick(id, cellsById.get(id) ?? []);
    setOpen(false);
  };
  const ticked = [...selected][0] as string | undefined;

  return (
    <div className="gl-modal">
      <input
        className={`sd-input gl-modal-text${showEmpty ? ' required-empty' : ''}`}
        type="text"
        readOnly
        value={labelText}
        placeholder={field.noneLabel ?? 'N/A'}
        aria-label={field.label}
      />
      {!readOnly && (
        <span className="gl-modal-actions">
          {!!value && (
            <button type="button" className="gl-modal-btn" title="Clear" aria-label="Clear" onClick={onClear}>
              <i className="ti ti-eraser" />
            </button>
          )}
          <button
            type="button"
            className="gl-modal-btn"
            title={`Find ${field.label}`}
            aria-label={`Find ${field.label}`}
            onClick={() => setOpen(true)}
          >
            <i className="ti ti-search" />
          </button>
        </span>
      )}
      <AppDialog
        open={open}
        title={field.label}
        width={820}
        className="dlg-center"
        maximizable
        onClose={() => setOpen(false)}
        footer={
          <>
            <button
              type="button"
              className="ca-btn ca-btn-solid"
              disabled={!ticked}
              onClick={() => {
                if (ticked) pick(ticked);
              }}
            >
              OK
            </button>
            <button type="button" className="ca-btn" onClick={() => setOpen(false)}>
              Cancel
            </button>
          </>
        }
      >
        <div className="gl-picker-search">
          <GridSearch value={search} onChange={setSearch} placeholder="Text to search…" />
        </div>
        <AppDataGrid<DialogRow>
          rows={filtered}
          columns={columns}
          getRowId={(o) => o.id}
          defaultPageSize={20}
          selectable
          selectedIds={selected}
          onSelectionChange={(next) => {
            const kept = singleSelection(selected, next);
            if (kept) setSelected(kept);
          }}
          onRowClick={(o) => pick(o.id)}
          emptyText="No matches"
        />
      </AppDialog>
    </div>
  );
}
